import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
} from '@angular/core';
import {ProfileService} from '../profile.service';
import {
    CumulativePeriod,
    IndividualCumulative,
    RaainApiRainsCumulativesDetailedResponse,
    RaainApiRainsCumulativesListResponse,
} from 'raain-model';

export interface CumulativeSelection {
    periodBegin: Date;
    periodEnd: Date;
    windowInMinutes: number;
    cumulativeId?: string;
    cumulativeDate?: Date;
}

@Component({
    selector: 'cumulative-selector',
    templateUrl: './cumulative-selector.component.html',
    styleUrls: ['./cumulative-selector.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CumulativeSelectorComponent implements OnInit {
    @Input() rainId: string;
    @Input() currentPeriodBegin: Date;
    @Input() currentPeriodEnd: Date;
    @Input() provider: string;
    @Input() timeStepInMinutes: number = 5;
    @Input() isAdmin: boolean = false;

    @Output() periodSelected = new EventEmitter<CumulativeSelection>();
    @Output() cancelled = new EventEmitter<void>();

    availablePeriods: CumulativePeriod[] = [];
    baseCumulatives: CumulativePeriod = null;
    loading = true;
    creating = false;
    creationProgress = '';
    errorMessage = '';

    coveragePercent = 0;
    canCreateNew = false;
    currentWindowMinutes = 0;

    expandedPeriods: Map<string, IndividualCumulative[]> = new Map();
    loadingExpanded: Set<string> = new Set();

    private readonly POLL_TIMEOUT_MS = 900000; // 900 seconds
    private readonly POLL_INTERVAL_MS = 3000;

    constructor(
        private profileService: ProfileService,
        private cdr: ChangeDetectorRef
    ) {}

    async ngOnInit() {
        this.currentWindowMinutes = Math.round(
            (this.currentPeriodEnd.getTime() - this.currentPeriodBegin.getTime()) / 60000
        );
        await this.loadAvailablePeriods();
    }

    async loadAvailablePeriods() {
        this.loading = true;
        this.errorMessage = '';
        this.cdr.markForCheck();

        try {
            // Fetch all cumulative periods (admin sees all, non-admin sees only customer-launched)
            const response = (await this.profileService.getCumulativePeriods(this.rainId, {
                provider: this.provider,
                forced: this.isAdmin,
            })) as RaainApiRainsCumulativesListResponse;

            // Filter for existing custom cumulatives (window > 0)
            this.availablePeriods = response.periods.filter((p) => p.windowInMinutes > 0);

            // Get base cumulatives (window = 0) to check coverage
            this.baseCumulatives = response.periods.find((p) => p.windowInMinutes === 0);

            this.calculateCoverage();
        } catch (e) {
            this.errorMessage = 'Failed to load cumulative periods';
            console.error('Error loading cumulative periods:', e);
        }

        this.loading = false;
        this.cdr.markForCheck();
    }

    calculateCoverage() {
        if (!this.baseCumulatives) {
            this.coveragePercent = 0;
            this.canCreateNew = false;
            return;
        }

        const baseBegin = new Date(this.baseCumulatives.periodBegin);
        const baseEnd = new Date(this.baseCumulatives.periodEnd);

        // Check if current period is within base coverage
        const currentInRange =
            this.currentPeriodBegin >= baseBegin && this.currentPeriodEnd <= baseEnd;

        if (!currentInRange) {
            this.coveragePercent = 0;
            this.canCreateNew = false;
            return;
        }

        // Calculate expected number of base cumulatives needed
        const expectedCount = Math.ceil(this.currentWindowMinutes / this.timeStepInMinutes);

        // Check if enough base cumulatives exist
        // We assume coverage is complete if count >= expected (simplified check)
        if (this.baseCumulatives.count >= expectedCount) {
            this.coveragePercent = 100;
            this.canCreateNew = true;
        } else {
            this.coveragePercent = Math.round((this.baseCumulatives.count / expectedCount) * 100);
            this.canCreateNew = this.coveragePercent >= 100;
        }
    }

    selectPeriod(period: CumulativePeriod) {
        this.periodSelected.emit({
            periodBegin: new Date(period.periodBegin),
            periodEnd: new Date(period.periodEnd),
            windowInMinutes: period.windowInMinutes,
        });
    }

    async toggleExpand(period: CumulativePeriod) {
        const key = `${period.windowInMinutes}-${period.provider}`;
        if (this.expandedPeriods.has(key)) {
            this.expandedPeriods.delete(key);
            this.cdr.markForCheck();
            return;
        }

        this.loadingExpanded.add(key);
        this.cdr.markForCheck();

        try {
            const response = (await this.profileService.getCumulativePeriods(this.rainId, {
                provider: this.provider,
                windowInMinutes: period.windowInMinutes,
                forced: this.isAdmin,
                detailed: true,
            })) as RaainApiRainsCumulativesDetailedResponse;

            this.expandedPeriods.set(key, response.cumulatives || []);
        } catch (e) {
            console.error('Error loading individual cumulatives:', e);
            this.expandedPeriods.set(key, []);
        }

        this.loadingExpanded.delete(key);
        this.cdr.markForCheck();
    }

    selectIndividualCumulative(item: IndividualCumulative) {
        const endDate = new Date(item.date);
        const beginDate = new Date(endDate.getTime() - item.windowInMinutes * 60000);

        this.periodSelected.emit({
            periodBegin: beginDate,
            periodEnd: endDate,
            windowInMinutes: item.windowInMinutes,
            cumulativeId: item.id,
            cumulativeDate: endDate,
        });
    }

    isExpanded(period: CumulativePeriod): boolean {
        return this.expandedPeriods.has(`${period.windowInMinutes}-${period.provider}`);
    }

    isLoadingExpanded(period: CumulativePeriod): boolean {
        return this.loadingExpanded.has(`${period.windowInMinutes}-${period.provider}`);
    }

    getExpandedItems(period: CumulativePeriod): IndividualCumulative[] {
        return this.expandedPeriods.get(`${period.windowInMinutes}-${period.provider}`) || [];
    }

    getItemPeriodBegin(item: IndividualCumulative): Date {
        const endDate = new Date(item.date);
        return new Date(endDate.getTime() - item.windowInMinutes * 60000);
    }

    async createNewPeriod() {
        if (!this.canCreateNew || this.creating) {
            return;
        }

        this.creating = true;
        this.creationProgress = 'Starting cumulative computation...';
        this.errorMessage = '';
        this.cdr.markForCheck();

        try {
            // Trigger cumulative creation
            const result = await this.profileService.createCumulativePeriod(this.rainId, {
                periodBegin: this.currentPeriodBegin,
                periodEnd: this.currentPeriodEnd,
                provider: this.provider,
                timeStepInMinutes: this.timeStepInMinutes,
            });

            if (!result) {
                throw new Error('Failed to trigger cumulative computation');
            }

            this.creationProgress = `Jobs queued. Polling for completion...`;
            this.cdr.markForCheck();

            // Poll for completion
            const success = await this.pollForCompletion();

            if (success) {
                this.periodSelected.emit({
                    periodBegin: this.currentPeriodBegin,
                    periodEnd: this.currentPeriodEnd,
                    windowInMinutes: this.currentWindowMinutes,
                });
            } else {
                this.errorMessage = 'Timeout waiting for cumulative computation';
            }
        } catch (e) {
            this.errorMessage = `Error: ${e.message || 'Unknown error'}`;
            console.error('Error creating cumulative:', e);
        }

        this.creating = false;
        this.cdr.markForCheck();
    }

    private async pollForCompletion(): Promise<boolean> {
        const startTime = Date.now();

        while (Date.now() - startTime < this.POLL_TIMEOUT_MS) {
            try {
                const progress = await this.profileService.getRainProgress(this.rainId);

                if (progress === 0) {
                    // Queue is empty, check if cumulative exists (admin sees all cumulatives)
                    const response = (await this.profileService.getCumulativePeriods(this.rainId, {
                        provider: this.provider,
                        windowInMinutes: this.currentWindowMinutes,
                        forced: this.isAdmin,
                    })) as RaainApiRainsCumulativesListResponse;

                    const found = response.periods.find(
                        (p) => p.windowInMinutes === this.currentWindowMinutes
                    );

                    if (found) {
                        return true;
                    }
                }

                const elapsed = Math.round((Date.now() - startTime) / 1000);
                this.creationProgress = `Computing... (${elapsed}s, queue: ${progress})`;
                this.cdr.markForCheck();

                await this.sleep(this.POLL_INTERVAL_MS);
            } catch (e) {
                console.warn('Poll error:', e);
                await this.sleep(this.POLL_INTERVAL_MS);
            }
        }

        return false;
    }

    private sleep(ms: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    cancel() {
        this.cancelled.emit();
    }

    formatPeriod(period: CumulativePeriod): string {
        const begin = new Date(period.periodBegin);
        const end = new Date(period.periodEnd);
        return `${begin.toLocaleString()} → ${end.toLocaleString()}`;
    }

    formatWindow(minutes: number): string {
        if (minutes < 60) {
            return `${minutes} min`;
        }
        const hours = minutes / 60;
        return hours === 1 ? '1 hour' : `${hours} hours`;
    }
}
